import type { UseMutationResult } from '@tanstack/react-query';
import { useIsMutating, useMutation } from '@tanstack/react-query';

import type {
    PositionArchitecture,
    PositionAttribute,
    PositionAttributeValue,
    PositionChartView,
} from '../../data-contract/position-management';
import type {
    CreatePositionAttributeInput,
    CreatePositionAttributeValueInput,
    CreatePositionChartViewInput,
    PositionArchitectureTarget,
    PositionAttributeTarget,
    PositionAttributeValueTarget,
    PositionChartViewTarget,
    SetPositionManagersCanEditInput,
    UpdatePositionAttributeInput,
    UpdatePositionAttributeValueInput,
    UpdatePositionChartViewInput,
} from '../../fetchers/position-management/position-management-fetchers';
import { unwrapResult } from '../../fetchers/unwrap-result';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { withDefaultArchitecture, withManagersCanEdit } from '../architecture-selection';
import {
    cacheUpdatedArchitecture,
    forgetDeletedArchitecture,
    invalidateCatalogCaches,
    invalidateWorkspaceCaches,
    refreshStaleArchitectures,
} from './invalidate-position-caches';
import { positionManagementKeys } from './query-keys';
import { useInvalidatingMutation } from './use-invalidating-mutation';

type ArchitectureScoped<Input> = Input & PositionArchitectureTarget;

const ATTRIBUTE_MUTATION_KEY = [...positionManagementKeys.all, 'attribute'];
const ATTRIBUTE_VALUE_MUTATION_KEY = [...positionManagementKeys.all, 'attribute-value'];
const CHART_VIEW_MUTATION_KEY = [...positionManagementKeys.all, 'chart-view'];

export const useIsAttributeMutating = (): boolean => useIsMutating({ mutationKey: ATTRIBUTE_MUTATION_KEY }) > 0;

export const useIsAttributeValueMutating = (): boolean =>
    useIsMutating({ mutationKey: ATTRIBUTE_VALUE_MUTATION_KEY }) > 0;

export const useIsChartViewMutating = (): boolean => useIsMutating({ mutationKey: CHART_VIEW_MUTATION_KEY }) > 0;

export const useCreatePositionArchitecture = (): UseMutationResult<
    PositionArchitecture,
    Error,
    { readonly name: string }
> => {
    const { createPositionArchitecture } = useApplicationFetchers();

    return useMutation({ mutationFn: (variables) => unwrapResult(createPositionArchitecture(variables)) });
};

export const useDeletePositionArchitecture = (): UseMutationResult<unknown, Error, PositionArchitectureTarget> => {
    const { deletePositionArchitecture } = useApplicationFetchers();

    return useInvalidatingMutation(deletePositionArchitecture, forgetDeletedArchitecture, {
        onError: refreshStaleArchitectures,
    });
};

export const useSetDefaultPositionArchitecture = (): UseMutationResult<
    PositionArchitecture,
    Error,
    PositionArchitectureTarget
> => {
    const { setDefaultPositionArchitecture } = useApplicationFetchers();

    return useInvalidatingMutation(setDefaultPositionArchitecture, cacheUpdatedArchitecture(withDefaultArchitecture), {
        onError: refreshStaleArchitectures,
    });
};

export const useSetPositionManagersCanEdit = (): UseMutationResult<
    PositionArchitecture,
    Error,
    SetPositionManagersCanEditInput
> => {
    const { setPositionManagersCanEdit } = useApplicationFetchers();

    return useInvalidatingMutation(setPositionManagersCanEdit, cacheUpdatedArchitecture(withManagersCanEdit), {
        onError: refreshStaleArchitectures,
    });
};

export const useCreatePositionAttribute = (): UseMutationResult<
    PositionAttribute,
    Error,
    CreatePositionAttributeInput
> => {
    const { createPositionAttribute } = useApplicationFetchers();

    return useInvalidatingMutation(createPositionAttribute, invalidateCatalogCaches, {
        mutationKey: ATTRIBUTE_MUTATION_KEY,
    });
};

export const useUpdatePositionAttribute = (): UseMutationResult<
    PositionAttribute,
    Error,
    UpdatePositionAttributeInput
> => {
    const { updatePositionAttribute } = useApplicationFetchers();

    return useInvalidatingMutation(updatePositionAttribute, invalidateCatalogCaches, {
        mutationKey: ATTRIBUTE_MUTATION_KEY,
    });
};

export const useDeletePositionAttribute = (): UseMutationResult<unknown, Error, PositionAttributeTarget> => {
    const { deletePositionAttribute } = useApplicationFetchers();

    return useInvalidatingMutation(deletePositionAttribute, invalidateCatalogCaches, {
        mutationKey: ATTRIBUTE_MUTATION_KEY,
    });
};

export const useCreatePositionAttributeValue = (): UseMutationResult<
    PositionAttributeValue,
    Error,
    CreatePositionAttributeValueInput
> => {
    const { createPositionAttributeValue } = useApplicationFetchers();

    return useInvalidatingMutation(
        createPositionAttributeValue,
        (queryClient, { request }) =>
            invalidateWorkspaceCaches(queryClient, { architectureId: request.architecture_id }),
        { mutationKey: ATTRIBUTE_VALUE_MUTATION_KEY },
    );
};

export const useUpdatePositionAttributeValue = (): UseMutationResult<
    PositionAttributeValue,
    Error,
    UpdatePositionAttributeValueInput
> => {
    const { updatePositionAttributeValue } = useApplicationFetchers();

    return useInvalidatingMutation(updatePositionAttributeValue, invalidateCatalogCaches, {
        mutationKey: ATTRIBUTE_VALUE_MUTATION_KEY,
    });
};

export const useDeletePositionAttributeValue = (): UseMutationResult<unknown, Error, PositionAttributeValueTarget> => {
    const { deletePositionAttributeValue } = useApplicationFetchers();

    return useInvalidatingMutation(deletePositionAttributeValue, invalidateCatalogCaches, {
        mutationKey: ATTRIBUTE_VALUE_MUTATION_KEY,
    });
};

export const useCreatePositionChartView = (): UseMutationResult<
    PositionChartView,
    Error,
    CreatePositionChartViewInput
> => {
    const { createPositionChartView } = useApplicationFetchers();

    return useInvalidatingMutation<CreatePositionChartViewInput, PositionChartView>(
        createPositionChartView,
        invalidateWorkspaceCaches,
        { mutationKey: CHART_VIEW_MUTATION_KEY },
    );
};

export const useUpdatePositionChartView = (): UseMutationResult<
    PositionChartView,
    Error,
    ArchitectureScoped<UpdatePositionChartViewInput>
> => {
    const { updatePositionChartView } = useApplicationFetchers();

    return useInvalidatingMutation<ArchitectureScoped<UpdatePositionChartViewInput>, PositionChartView>(
        updatePositionChartView,
        invalidateWorkspaceCaches,
        { mutationKey: CHART_VIEW_MUTATION_KEY },
    );
};

export const useDeletePositionChartView = (): UseMutationResult<
    unknown,
    Error,
    ArchitectureScoped<PositionChartViewTarget>
> => {
    const { deletePositionChartView } = useApplicationFetchers();

    return useInvalidatingMutation<ArchitectureScoped<PositionChartViewTarget>, unknown>(
        deletePositionChartView,
        invalidateWorkspaceCaches,
        { mutationKey: CHART_VIEW_MUTATION_KEY },
    );
};
